<template>
  <div class="login-button-container">
    <h2 class="login-title">
      Войти в личный кабинет
    </h2>

    <p class="login-description">
      Добро пожаловать в HR3 — платформу, где талантливые люди
      и прогрессивные компании находят друг друга быстрее и проще,
      чем когда-либо.
    </p>

    <button
      class="login-button apple-button"
      type="button"
      @click="emit('AppleSignInButtonPressed')"
    >
      <img
        src="/apple_logo.png"
        alt="Apple Logo"
        class="button-icon"
      />

      Продолжить через Apple
    </button>

    <button
      class="google-login-button"
      type="button"
      @click="emit('GoogleSignInButtonPressed')"
    >
      <img
        src="/google_logo.png"
        alt="Google Logo"
        class="google-button-icon"
      />

      Продолжить через Google
    </button>

    <div class="tooltip-wrapper">
      <button
        class="login-button phone-button"
        type="button"
        :class="{ disabled: isRussian }"
        :disabled="isRussian"
        @click="emit('PhoneNumberSignInPressed')"
      >
        Войти по номеру телефона
      </button>

      <div
        v-if="isRussian"
        class="tooltip"
      >
        Сейчас вход по телефону недоступен в вашем регионе
      </div>
    </div>

    <p class="or-text">
      или
    </p>

    <div class="input-container">
      <TextField
        v-model="email"
        type="email"
        placeholder="Email"
      />

      <SecureTextField
        v-model="password"
        placeholder="Пароль"
        @enterPressed="
          emit('EmailSignInPressed', {
            email,
            password,
          })
        "
      />
    </div>

    <button
      class="email-login-button"
      type="button"
      :disabled="!isLoginEnabled"
      @click="
        emit('EmailSignInPressed', {
          email,
          password,
        })
      "
    >
      Войти
    </button>

    <transition name="forgot-password">
      <button
        v-if="showForgotPassword"
        class="forgot-password-button"
        type="button"
        @click="
          emit('ForgotPasswordPressed', {
            email,
          })
        "
      >
        Забыли пароль? Восстановить
      </button>
    </transition>

    <div class="register-container">
      <span>
        Нет аккаунта?
      </span>

      <span
        class="register-link"
        @click="emit('GoToRegister')"
      >
        Зарегистрироваться
      </span>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  onMounted,
  ref,
} from "vue";

import TextField from "../TextField.vue";
import SecureTextField from "../SecureTextField.vue";

const emit = defineEmits([
  "GoogleSignInButtonPressed",
  "AppleSignInButtonPressed",
  "PhoneNumberSignInPressed",
  "EmailSignInPressed",
  "ForgotPasswordPressed",
  "GoToRegister",
]);

const props = defineProps({
  showForgotPassword: {
    type: Boolean,
    default: false,
  },
});

const email = ref("");
const password = ref("");
const isRussian = ref(false);

const isLoginEnabled = computed(() => {
  return (
    email.value.length > 0 &&
    password.value.length > 0
  );
});

onMounted(() => {
  const lang =
    navigator.language ||
    navigator.userLanguage;

  if (
    lang &&
    lang.toLowerCase().startsWith("ru")
  ) {
    isRussian.value = true;
  }
});
</script>

<style scoped>

.login-button-container {
  width: 385px;
  min-height: 610px;
  height: 650px;

  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 20px;
  border-radius: 15px;
  background-color: #2b2b2b;
}

[data-theme="light"] .login-button-container {
  background-color: #f7f7f7;
}

[data-theme="light"] .login-title,
[data-theme="light"] .login-description,
[data-theme="light"] .or-text,
[data-theme="light"] .register-container {
  color: #111111;
}

[data-theme="light"] .register-link {
  color: #2173e5;
}

.login-title {
  margin-top: 0;
  margin-bottom: 0;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  letter-spacing: -0.4px;
  text-align: center;

  color: #ffffff;
}

.login-description {
  max-width: 80%;

  margin-bottom: 20px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.4;
  letter-spacing: -0.4px;
  text-align: center;

  color: #ffffff;
}

.login-button,
.google-login-button {
  position: relative;

  width: 336px;
  height: 50px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 25px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;

  cursor: pointer;

  overflow: hidden;

  -webkit-tap-highlight-color: transparent;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease,
    opacity 0.2s ease;
}

.login-button {
  margin-bottom: 20px;
}

.login-button:hover:not(:disabled),
.google-login-button:hover:not(:disabled) {
  transform: translateY(-2px);
}

.login-button:active:not(:disabled),
.google-login-button:active:not(:disabled) {
  transform: translateY(0) scale(0.985);
}

.apple-button {
  color: #ffffff;

  border: 1px solid rgba(255, 255, 255, 0.08);

  background:
    linear-gradient(
      180deg,
      #181818 0%,
      #050505 100%
    );

  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

.apple-button:hover:not(:disabled) {
  background:
    linear-gradient(
      180deg,
      #222222 0%,
      #0b0b0b 100%
    );

  box-shadow:
    0 12px 26px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.button-icon {
  width: 18px;
  height: 20px;

  margin-right: 20px;
  margin-top: -4px;

  object-fit: contain;
}

.google-login-button {
  color: #171717;

  border: 1px solid rgba(0, 0, 0, 0.08);

  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f7f7f7 100%
    );

  box-shadow:
    0 7px 18px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.google-login-button:hover:not(:disabled) {
  border-color:
    rgba(33, 115, 229, 0.22);

  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f2f7fd 100%
    );

  box-shadow:
    0 11px 24px rgba(33, 115, 229, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.96);
}

.google-button-icon {
  width: 20px;
  height: 20px;

  margin-right: 20px;
  margin-top: -4px;

  object-fit: contain;
}

.phone-button {
  margin-top: 28px;

  color: #ffffff;

  border: 1px solid rgba(99, 164, 255, 0.22);

  background:
    linear-gradient(
      110deg,
      #2887f7 0%,
      #2173e5 48%,
      #204f90 100%
    );

  box-shadow:
    0 9px 22px rgba(33, 115, 229, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);
}

.phone-button:hover:not(:disabled) {
  box-shadow:
    0 13px 28px rgba(33, 115, 229, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.phone-button.disabled,
.phone-button:disabled {
  position: relative;

  color: rgba(255, 255, 255, 0.48);

  cursor: not-allowed;
  pointer-events: auto;

  opacity: 1;

  border-color:
    rgba(255, 255, 255, 0.05);

  background:
    linear-gradient(
      180deg,
      #464646 0%,
      #393939 100%
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.or-text {
  margin-top: 0;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
  text-align: center;
  color: #ffffff;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.email-login-button {
  position: relative;

  width: 260px;
  height: 50px;

  overflow: hidden;

  margin-top: 20px;

  border: 1px solid rgba(99, 164, 255, 0.2);
  border-radius: 25px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;

  color: #ffffff;

  background:
    linear-gradient(
      110deg,
      #2887f7 0%,
      #2173e5 48%,
      #204f90 100%
    );

  box-shadow:
    0 10px 24px rgba(33, 115, 229, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);

  cursor: pointer;

  -webkit-tap-highlight-color: transparent;

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    opacity 0.2s ease;
}

.email-login-button::before {
  content: "";

  position: absolute;

  top: -30%;
  bottom: -30%;
  left: -45%;

  width: 32%;

  pointer-events: none;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );

  transform: skewX(-20deg);

  transition:
    left 0.55s ease;
}

.email-login-button:hover:not(:disabled) {
  transform: translateY(-2px);

  box-shadow:
    0 14px 30px rgba(33, 115, 229, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.email-login-button:hover:not(:disabled)::before {
  left: 120%;
}

.email-login-button:active:not(:disabled) {
  transform: translateY(0) scale(0.985);
}

.email-login-button:disabled {
  color:
    rgba(255, 255, 255, 0.34);

  cursor: not-allowed;

  opacity: 1;

  border-color:
    rgba(255, 255, 255, 0.04);

  background:
    linear-gradient(
      180deg,
      #474747 0%,
      #3d3d3d 100%
    );

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.email-login-button:disabled::before {
  display: none;
}

.register-container {
  margin-top: 20px;
  padding-bottom: 10px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
  letter-spacing: -0.4px;
  text-align: center;

  color: #ffffff;
}

.register-link {
  margin-left: 5px;

  color: #2887f7;

  cursor: pointer;

  transition:
    opacity 0.2s ease,
    text-decoration-color 0.2s ease;
}

.register-link:hover {
  opacity: 0.8;

  text-decoration: underline;
}

.phone-disabled-note {
  margin-top: -15px;
  margin-bottom: 15px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;

  color: #ff6666;
}

.tooltip-wrapper {
  position: relative;

  width: 100%;

  display: flex;
  justify-content: center;

  margin-left: 0;
}

.tooltip {
  position: absolute;
  z-index: 10;

  top: -46px;
  left: 50%;

  padding: 9px 12px;

  white-space: nowrap;

  border:
    1px solid
    rgba(255, 255, 255, 0.08);

  border-radius: 10px;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 11px;

  color:
    rgba(255, 255, 255, 0.9);

  background:
    rgba(25, 25, 25, 0.96);

  box-shadow:
    0 10px 28px
    rgba(0, 0, 0, 0.28);

  opacity: 0;
  pointer-events: none;

  transform:
    translate(-50%, 5px);

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.tooltip::after {
  content: "";

  position: absolute;

  top: 100%;
  left: 50%;

  width: 8px;
  height: 8px;

  border-right:
    1px solid
    rgba(255, 255, 255, 0.08);

  border-bottom:
    1px solid
    rgba(255, 255, 255, 0.08);

  background:
    rgba(25, 25, 25, 0.96);

  transform:
    translate(-50%, -4px)
    rotate(45deg);
}

.tooltip-wrapper:hover .tooltip {
  opacity: 1;

  transform:
    translate(-50%, 0);
}

[data-theme="light"] .apple-button {
  border-color:
    rgba(0, 0, 0, 0.08);
}

[data-theme="light"] .google-login-button {
  color: #171717;

  border-color:
    rgba(0, 0, 0, 0.08);

  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f8f8f8 100%
    );
}

[data-theme="light"] .phone-button:disabled,
[data-theme="light"] .phone-button.disabled {
  color:
    rgba(17, 17, 17, 0.38);

  border-color:
    rgba(0, 0, 0, 0.05);

  background:
    linear-gradient(
      180deg,
      #e5e5e5 0%,
      #dcdcdc 100%
    );

  box-shadow:
    inset 0 1px 0
    rgba(255, 255, 255, 0.55);
}

[data-theme="light"] .email-login-button {
  color: #ffffff;

  background:
    linear-gradient(
      110deg,
      #2887f7 0%,
      #2173e5 48%,
      #204f90 100%
    );
}

[data-theme="light"] .email-login-button:disabled {
  color:
    rgba(17, 17, 17, 0.32);

  border-color:
    rgba(0, 0, 0, 0.04);

  background:
    linear-gradient(
      180deg,
      #dedede 0%,
      #d3d3d3 100%
    );

  box-shadow:
    inset 0 1px 0
    rgba(255, 255, 255, 0.55);
}

@media (max-width: 768px) {
  .login-button-container {
    width: 340px;
    height: auto;

    padding: 18px;
  }

  .login-button,
  .google-login-button {
    width: 300px;
    height: 46px;
  }

  .phone-button {
    width: 300px;
  }

  .email-login-button {
    width: 220px;
    height: 46px;
  }

  .login-description {
    font-size: 11px;
  }

  .tooltip-wrapper {
    display: flex;
    justify-content: center;

    margin-left: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-button,
  .google-login-button,
  .email-login-button,
  .email-login-button::before,
  .tooltip {
    transition-duration: 0.01ms !important;
  }
}

.forgot-password-button {
  margin-top: 14px;
  padding: 4px 10px;

  border: 0;
  background: transparent;

  color: #2887f7;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 12px;
  letter-spacing: -0.3px;

  cursor: pointer;

  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.forgot-password-button:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.forgot-password-button:active {
  transform: scale(0.97);
}

.forgot-password-enter-active,
.forgot-password-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.forgot-password-enter-from,
.forgot-password-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>